import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";
import { Dialog } from "./Dialog";
import { Button } from "./Button";
import "./ConfirmDialog.scss";
import "./Overlays.scss";

// ---------- confirm() / alert() as brand dialogs ----------

export type ConfirmOptions = {
  message: React.ReactNode;
  title?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  /** danger = red button and a warning icon; primary = orange (default) */
  tone?: "danger" | "primary";
};

export type AlertOptions = {
  message: React.ReactNode;
  title?: React.ReactNode;
  okLabel?: React.ReactNode;
};

type Pending =
  | { kind: "confirm"; options: ConfirmOptions; resolve: (ok: boolean) => void }
  | { kind: "alert"; options: AlertOptions; resolve: () => void };

type DialogsApi = {
  /** The brand replacement for window.confirm: resolves true on confirm, false on cancel. */
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
  /** The brand replacement for window.alert: resolves when dismissed. */
  alert: (options: AlertOptions | string) => Promise<void>;
};

const DialogsContext = createContext<DialogsApi | null>(null);

/**
 * `const { confirm, alert } = useDialogs();` then `if (!(await confirm("Delete this court?"))) return;` —
 * the same flow as window.confirm / window.alert, in a brand dialog. Needs <UiProvider> (mounted in App).
 */
export function useDialogs(): DialogsApi {
  const api = useContext(DialogsContext);
  if (!api) throw new Error("useDialogs needs <UiProvider> above it");
  return api;
}

function DialogsHost({ pending, close }: { pending: Pending | null; close: (result: boolean) => void }) {
  const [shown, setShown] = useState<Pending | null>(pending);
  useEffect(() => {
    if (pending) setShown(pending);
  }, [pending]);
  const open = pending !== null;
  if (!shown) return null;

  if (shown.kind === "alert") {
    const o = shown.options;
    return (
      <Dialog
        open={open}
        onClose={() => close(true)}
        title={o.title}
        size="sm"
        footer={<Button onClick={() => close(true)} autoFocus>{o.okLabel ?? "OK"}</Button>}
      >
        <div className="ui-confirm__message">{o.message}</div>
      </Dialog>
    );
  }

  const o = shown.options;
  const danger = o.tone === "danger";
  return (
    <Dialog
      open={open}
      onClose={() => close(false)}
      title={o.title ?? "Please confirm"}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)}>
            {o.cancelLabel ?? "Cancel"}
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={() => close(true)} autoFocus>
            {o.confirmLabel ?? "OK"}
          </Button>
        </>
      }
    >
      <div className={`ui-confirm ui-confirm--${danger ? "danger" : "primary"}`}>
        {danger ? (
          <span className="ui-confirm__icon" aria-hidden="true">
            <AlertTriangle size={20} />
          </span>
        ) : null}
        <div className="ui-confirm__text">
          <div className="ui-confirm__message">{o.message}</div>
        </div>
      </div>
    </Dialog>
  );
}

// ---------- tooltips: any element with data-tooltip="…" ----------

type Tip = { text: string; rect: DOMRect } | null;

/** One floating tooltip for the whole app: hover or focus any element that has `data-tooltip`. */
function TooltipLayer() {
  const [tip, setTip] = useState<Tip>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: "hidden" });
  const bubble = useRef<HTMLDivElement>(null);
  const timer = useRef<number>();

  useEffect(() => {
    const find = (t: EventTarget | null) => (t instanceof Element ? (t.closest("[data-tooltip]") as HTMLElement | null) : null);
    const show = (e: Event) => {
      const el = find(e.target);
      if (!el) return;
      const text = el.getAttribute("data-tooltip");
      if (!text) return;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setTip({ text, rect: el.getBoundingClientRect() }), e.type === "focusin" ? 0 : 350);
    };
    const hide = (e: Event) => {
      const el = find(e.target);
      const to = (e as MouseEvent).relatedTarget;
      if (el && to instanceof Node && el.contains(to)) return;
      window.clearTimeout(timer.current);
      setTip(null);
    };
    const clear = () => {
      window.clearTimeout(timer.current);
      setTip(null);
    };
    document.addEventListener("mouseover", show);
    document.addEventListener("mouseout", hide);
    document.addEventListener("focusin", show);
    document.addEventListener("focusout", hide);
    document.addEventListener("mousedown", clear);
    window.addEventListener("scroll", clear, true);
    return () => {
      document.removeEventListener("mouseover", show);
      document.removeEventListener("mouseout", hide);
      document.removeEventListener("focusin", show);
      document.removeEventListener("focusout", hide);
      document.removeEventListener("mousedown", clear);
      window.removeEventListener("scroll", clear, true);
      window.clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    if (!tip || !bubble.current) return;
    const b = bubble.current.getBoundingClientRect();
    const above = tip.rect.top - b.height - 8 >= 4;
    const left = Math.max(6, Math.min(tip.rect.left + tip.rect.width / 2 - b.width / 2, window.innerWidth - b.width - 6));
    setStyle({ left, top: above ? tip.rect.top - b.height - 8 : tip.rect.bottom + 8 });
  }, [tip]);

  if (!tip) return null;
  return createPortal(
    <div ref={bubble} className="ui-tooltip" role="tooltip" style={style}>
      {tip.text}
    </div>,
    document.body
  );
}

// ---------- the provider ----------

/** Mount once at the app root: brand confirm/alert dialogs (useDialogs) and tooltips (data-tooltip). */
export function UiProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const queue = useRef<Pending[]>([]);

  const next = useCallback(() => setPending(queue.current.shift() ?? null), []);
  const push = useCallback(
    (p: Pending) => {
      queue.current.push(p);
      setPending((current) => current ?? queue.current.shift() ?? null);
    },
    []
  );

  const api = React.useMemo<DialogsApi>(
    () => ({
      confirm: (options) =>
        new Promise<boolean>((resolve) =>
          push({ kind: "confirm", options: typeof options === "string" ? { message: options } : options, resolve })
        ),
      alert: (options) =>
        new Promise<void>((resolve) => push({ kind: "alert", options: typeof options === "string" ? { message: options } : options, resolve })),
    }),
    [push]
  );

  const close = (result: boolean) => {
    if (!pending) return;
    if (pending.kind === "confirm") pending.resolve(result);
    else pending.resolve();
    next();
  };

  return (
    <DialogsContext.Provider value={api}>
      {children}
      <DialogsHost pending={pending} close={close} />
      <TooltipLayer />
    </DialogsContext.Provider>
  );
}

export default UiProvider;
